import { TableText } from "@/components/table-tools";
import { useState } from "react";
import { useQuery } from "@connectrpc/connect-query";

import {
  adminListAccounts,
  adminListDevices,
  adminListWorkers,
  adminTrafficStats,
} from "@/gen/nodebase/v1/admin-AdminService_connectquery";
import type { AdminTrafficHour } from "@/gen/nodebase/v1/admin_pb";
import { Transport } from "@/gen/nodebase/v1/worker_pb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyNote, ErrorNote, Loading, PageHeader } from "@/components/page";
import { FleetScope } from "@/components/scope";
import { HourlyTrafficChart } from "@/components/hourly-traffic-chart";
import {
  ago,
  fmtBitrate,
  fmtBytes,
  fmtDateTime,
  fmtDuration,
  transportLabel,
  tunnelLive,
} from "@/lib/format";

const RANGES = [
  { days: 1, label: "24h" },
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
] as const;
const COMPACT_ROWS = 10;

// The operational traffic view follows authority: live heartbeat rates, the
// existing Worker/Edge hourly ledgers, and global anonymous routing signals.
// Account filters apply only where storage has an account dimension; endpoint
// and destination aggregates deliberately stay global.
export function AdminTraffic() {
  const [days, setDays] = useState<number>(1);
  const [accountId, setAccountId] = useState("");
  const [focus, setFocus] = useState<"hourly" | "live">("hourly");
  const accounts = useQuery(adminListAccounts, {}, { retry: false });
  const selectedAccount = accounts.data?.accounts.find((a) => a.id === accountId);
  const stats = useQuery(adminTrafficStats, { days, accountId }, { retry: false });
  const usage = stats.data?.usage ?? [];
  const dests = stats.data?.dests ?? [];
  const domains = dests.filter((d) => d.kind === "domain");
  const regions = dests.filter((d) => d.kind !== "domain");

  return (
    <>
      <PageHeader
        title="Traffic"
        scope={<FleetScope noun="Metered worker egress" />}
      />
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Select
          aria-label="Account filter"
          className="w-auto min-w-56"
          value={accountId}
          onChange={(event) => setAccountId(event.target.value)}
        >
          <option value="">All accounts</option>
          {(accounts.data?.accounts ?? []).map((account) => (
            <option key={account.id} value={account.id}>
              {account.email}
            </option>
          ))}
        </Select>
        <div className="flex items-center gap-1" aria-label="Traffic range">
        {RANGES.map((range) => (
          <Button
            key={range.days}
            variant={days === range.days ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setDays(range.days)}
          >
            {range.label}
          </Button>
        ))}
        </div>
      </div>
      <ErrorNote error={accounts.error ?? stats.error} />
      <div
        className="mb-4 inline-flex rounded-lg border bg-muted p-1"
        role="tablist"
        aria-label="Traffic focus"
      >
        <Button
          id="traffic-hourly-tab"
          className="min-w-24"
          role="tab"
          aria-selected={focus === "hourly"}
          aria-controls="traffic-hourly-panel"
          variant={focus === "hourly" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setFocus("hourly")}
        >
          By hour
        </Button>
        <Button
          id="traffic-live-tab"
          className="min-w-24"
          role="tab"
          aria-selected={focus === "live"}
          aria-controls="traffic-live-panel"
          variant={focus === "live" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setFocus("live")}
        >
          Live
        </Button>
      </div>
      {focus === "live" && (
        <div
          id="traffic-live-panel"
          role="tabpanel"
          aria-labelledby="traffic-live-tab"
        >
          <LiveRates ownerEmail={selectedAccount?.email} />
        </div>
      )}
      {stats.isPending ? (
        <Loading />
      ) : stats.isError ? null : (
        <>
          {focus === "hourly" && (
            <div
              id="traffic-hourly-panel"
              role="tabpanel"
              aria-labelledby="traffic-hourly-tab"
            >
              <HourlyTraffic rows={stats.data?.hours ?? []} days={days} />
            </div>
          )}

          <h2 className="mb-3 text-base font-semibold tracking-tight">
            Usage by workspace
          </h2>
          <p className="mb-3 text-sm text-muted-foreground">
            Complete egress from the hourly ledgers: Worker-native traffic plus
            Managed Edge Exit. Totals carry no destination information.
          </p>
          {usage.length === 0 ? (
            <EmptyNote>No metered traffic in this range.</EmptyNote>
          ) : (
            <Table aria-label="Usage by workspace">
              <TableHeader>
                <TableRow>
                  <TableHead>Workspace &amp; owner</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Source split</TableHead>
                  <TableHead>Connections</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usage.map((u) => (
                  <TableRow key={u.workspaceId} data-testid="usage-row">
                    <TableCell>
                      <div className="font-medium"><TableText value={u.ownerEmail || "—"} /></div>
                      <code className="text-xs">{u.workspaceId}</code>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      <div>{fmtBytes(u.upBytes + u.downBytes)}</div>
                      <div
                        className="text-xs text-muted-foreground"
                        title="Upload / download split"
                      >
                        {fmtBytes(u.upBytes)} ↑ · {fmtBytes(u.downBytes)} ↓
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums text-xs">
                      <div>Worker {fmtBytes(u.workerUpBytes + u.workerDownBytes)}</div>
                      <div>Edge {fmtBytes(u.edgeUpBytes + u.edgeDownBytes)}</div>
                    </TableCell>
                    <TableCell>{String(u.connections)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <WgTunnels ownerEmail={selectedAccount?.email} />
          <EndpointStats rows={stats.data?.endpoints ?? []} />

          <h2 className="mb-3 mt-8 text-base font-semibold tracking-tight">
            Worker egress destinations (global, anonymous)
          </h2>
          <p className="mb-3 text-sm text-muted-foreground">
            These are Worker-native egress buckets, not Managed Edge Exit
            destinations. They remain global when an account filter is selected:
            the server stores no
            user↔destination pair, so per-workspace destination views cannot
            exist. Domains are eTLD+1; regions are literal-IP targets resolved
            by country (?? = no GeoIP database). Dial latency and failure
            counts are the routing-optimization signal: a destination that is
            slow or times out through the worker is a candidate for a direct
            rule.
          </p>
          {dests.length === 0 ? (
            <EmptyNote>No destination stats in this range.</EmptyNote>
          ) : (
            <div className="grid gap-8 md:grid-cols-2">
              <DestTable title="Top domains" rows={domains} />
              <DestTable title="Regions & other" rows={regions} />
            </div>
          )}
        </>
      )}
    </>
  );
}

function HourlyTraffic({ rows, days }: { rows: AdminTrafficHour[]; days: number }) {
  const [expanded, setExpanded] = useState(false);
  const sorted = [...rows].sort((a, b) =>
    Number(b.windowStartUnix - a.windowStartUnix),
  );
  const visible = expanded ? sorted : sorted.slice(0, 24);
  const workerBytes = rows.reduce(
    (sum, hour) => sum + hour.workerUpBytes + hour.workerDownBytes,
    0n,
  );
  const edgeBytes = rows.reduce(
    (sum, hour) => sum + hour.edgeUpBytes + hour.edgeDownBytes,
    0n,
  );
  const connections = rows.reduce(
    (sum, hour) => sum + hour.workerConnections + hour.edgeConnections,
    0n,
  );
  const currentHour = Math.floor(Date.now() / 3_600_000) * 3600;

  return (
    <>
      <h2 className="mb-3 text-base font-semibold tracking-tight">
        Traffic by hour
      </h2>
      <p className="mb-3 text-sm text-muted-foreground">
        Existing UTC-hour ledgers, split by the system that actually carried
        the bytes. The current Edge hour updates on heartbeat; Worker windows
        close at the hour boundary, while the Live section above covers now.
      </p>
      <div className="mb-3 grid gap-2 text-sm sm:grid-cols-3" data-testid="hourly-summary">
        <div className="rounded-md border p-3">
          <div className="font-medium tabular-nums">{fmtBytes(workerBytes)}</div>
          <div className="text-muted-foreground">Worker egress</div>
        </div>
        <div className="rounded-md border p-3">
          <div className="font-medium tabular-nums">{fmtBytes(edgeBytes)}</div>
          <div className="text-muted-foreground">Managed Edge Exit</div>
        </div>
        <div className="rounded-md border p-3">
          <div className="font-medium tabular-nums">{String(connections)}</div>
          <div className="text-muted-foreground">Connections</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyNote>No hourly traffic in this range.</EmptyNote>
      ) : (
        <>
          <HourlyTrafficChart rows={rows} days={days} />
          <Table aria-label="Traffic by hour">
            <TableHeader>
              <TableRow>
                <TableHead>Hour</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Worker egress</TableHead>
                <TableHead>Managed Edge Exit</TableHead>
                <TableHead>Connections</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((hour) => {
                const worker = hour.workerUpBytes + hour.workerDownBytes;
                const edge = hour.edgeUpBytes + hour.edgeDownBytes;
                const isCurrent = Number(hour.windowStartUnix) === currentHour;
                return (
                  <TableRow key={String(hour.windowStartUnix)} data-testid="hourly-row">
                    <TableCell>
                      {fmtDateTime(hour.windowStartUnix)}{" "}
                      {isCurrent && <Badge variant="secondary">current</Badge>}
                    </TableCell>
                    <TableCell className="tabular-nums">{fmtBytes(worker + edge)}</TableCell>
                    <TableCell className="tabular-nums">{fmtBytes(worker)}</TableCell>
                    <TableCell className="tabular-nums">{fmtBytes(edge)}</TableCell>
                    <TableCell className="tabular-nums">
                      {String(hour.workerConnections + hour.edgeConnections)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {rows.length > 24 && (
            <Button
              className="mt-2"
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? "Show latest 24 hours" : `Show all ${rows.length} hours (${days}d)`}
            </Button>
          )}
        </>
      )}
      <div className="mb-8" />
    </>
  );
}

// LiveRates is the "who is on the link right now" view: per-worker tunnel
// rates derived from consecutive heartbeat samples (~30s resolution),
// repolled every 15s. The total sits against the host's configured link
// capacity — the number this page exists to protect.
function LiveRates({ ownerEmail }: { ownerEmail?: string }) {
  const [expanded, setExpanded] = useState(false);
  const workers = useQuery(
    adminListWorkers,
    {},
    { retry: false, refetchInterval: 15000 },
  );
  const rows = (workers.data?.workers ?? []).filter(
    (r) =>
      r.trafficReportedUnix > 0n &&
      (!ownerEmail || r.ownerEmail === ownerEmail),
  );
  const linkMbps = workers.data?.linkMbps ?? 0;
  const fresh = rows.filter(
    (r) => Date.now() / 1000 - Number(r.trafficReportedUnix) < 90,
  );
  const sorted = [...rows].sort((a, b) => {
    const aFresh = Date.now() / 1000 - Number(a.trafficReportedUnix) < 90;
    const bFresh = Date.now() / 1000 - Number(b.trafficReportedUnix) < 90;
    if (aFresh !== bFresh) return aFresh ? -1 : 1;
    const aRate = a.wgRxRateBytesSec + a.wgTxRateBytesSec;
    const bRate = b.wgRxRateBytesSec + b.wgTxRateBytesSec;
    if (aRate !== bRate) return aRate > bRate ? -1 : 1;
    return Number(b.trafficReportedUnix - a.trafficReportedUnix);
  });
  const visible = expanded ? sorted : sorted.slice(0, COMPACT_ROWS);
  const sum = (pick: (r: (typeof rows)[number]) => bigint) =>
    fresh.reduce(
      (acc, r) => (pick(r) >= 0n ? acc + Number(pick(r)) : acc),
      0,
    );
  const totalRx = sum((r) => r.wgRxRateBytesSec);
  const totalTx = sum((r) => r.wgTxRateBytesSec);
  const utilizationPct =
    linkMbps > 0
      ? Math.round(((Math.max(totalRx, totalTx) * 8) / (linkMbps * 1e6)) * 100)
      : null;

  return (
    <>
      <h2 className="mb-3 text-base font-semibold tracking-tight">Live</h2>
      <p className="mb-3 text-sm text-muted-foreground">
        Per-worker tunnel rate from heartbeat deltas (~30s resolution,
        auto-refreshes). Includes everything the tunnel carries — proxied
        egress, SSH, sync.
      </p>
      {workers.isPending ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyNote>No workers have reported traffic yet.</EmptyNote>
      ) : (
        <>
          <Table aria-label="Live traffic">
            <TableHeader>
              <TableRow>
                <TableHead>Worker &amp; sample</TableHead>
                <TableHead title="Worker receiving from devices / sending to devices">
                  Throughput
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((r) => (
                <TableRow key={r.worker?.id} data-testid="live-row">
                  <TableCell>
                    <code className="text-xs">{r.worker?.id}</code>
                    <div className="text-xs text-muted-foreground">
                      <TableText value={r.ownerEmail || "—"} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      sampled {ago(r.trafficReportedUnix)}
                    </div>
                  </TableCell>
                  <TableCell className="tabular-nums">
                    <div>{fmtBitrate(r.wgRxRateBytesSec)} in</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtBitrate(r.wgTxRateBytesSec)} out
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="live-total">
            {ownerEmail ? "Account" : "Fleet"} total: {fmtBitrate(totalRx)} in / {fmtBitrate(totalTx)} out
            {utilizationPct !== null && (
              <>
                {" "}
                — {utilizationPct}% of the {linkMbps} Mbps link
                {utilizationPct >= 80 && (
                  <span className="text-amber-700 dark:text-amber-400">
                    {" "}
                    (saturating)
                  </span>
                )}
              </>
            )}
          </p>
          {sorted.length > COMPACT_ROWS && (
            <Button
              className="mt-2"
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Show top 10" : `Show all ${sorted.length} workers`}
            </Button>
          )}
        </>
      )}
      <div className="mb-8" />
    </>
  );
}

// EndpointStats ranks the data-plane endpoints by what they actually deliver
// over the selected range: occupancy time, how often devices rotated away
// (the instability vote), and attributed bytes. This is the read side of the
// camouflage-inventory bet — whether :443/:3478 hold up — and the input for
// candidate-pool tuning. An endpoint nobody tried has no row; that is
// "untested", never "stable".
function EndpointStats({
  rows,
}: {
  rows: {
    host: string;
    port: number;
    transport: number;
    reports: bigint;
    liveSeconds: bigint;
    rotationsAway: bigint;
    rxBytes: bigint;
    txBytes: bigint;
  }[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [sortBy, setSortBy] = useState<"traffic" | "rotations">("traffic");
  const sorted = [...rows].sort((a, b) => {
    if (sortBy === "rotations" && a.rotationsAway !== b.rotationsAway) {
      return a.rotationsAway > b.rotationsAway ? -1 : 1;
    }
    const aTraffic = a.rxBytes + a.txBytes;
    const bTraffic = b.rxBytes + b.txBytes;
    if (aTraffic !== bTraffic) return aTraffic > bTraffic ? -1 : 1;
    if (a.liveSeconds !== b.liveSeconds) {
      return a.liveSeconds > b.liveSeconds ? -1 : 1;
    }
    return `${a.host}:${a.port}`.localeCompare(`${b.host}:${b.port}`);
  });
  const visible = expanded ? sorted : sorted.slice(0, COMPACT_ROWS);

  return (
    <>
      <h2 className="mb-3 text-base font-semibold tracking-tight">
        WG endpoints
      </h2>
      <p className="mb-3 text-sm text-muted-foreground">
        Which endpoints hold tunnels and carry bytes, from device attachment
        reports and per-device counter deltas. Rotations count devices leaving
        a path that died under them; mean hold is tunnel time per uninterrupted
        stay. This is a global routing signal and is not narrowed by the account
        filter. Untried endpoints are absent, not stable.
      </p>
      {rows.length > 1 && (
        <div className="mb-2 flex items-center gap-1" aria-label="Endpoint sort">
          <Button
            variant={sortBy === "traffic" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setSortBy("traffic")}
          >
            Traffic
          </Button>
          <Button
            variant={sortBy === "rotations" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setSortBy("rotations")}
          >
            Rotations
          </Button>
        </div>
      )}
      {rows.length === 0 ? (
        <EmptyNote>No endpoint data in this range.</EmptyNote>
      ) : (
        <Table aria-label="WG endpoints">
          <TableHeader>
            <TableRow>
              <TableHead>Endpoint</TableHead>
              <TableHead title="Mean hold is tunnel time per uninterrupted stay; the subline shows total live tunnel time and rotations away">
                Stability
              </TableHead>
              <TableHead title="Bytes attributed from per-peer heartbeat deltas: rx = device upload">
                Traffic
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((e) => (
              <TableRow
                key={`${e.host}:${e.port}/${e.transport}`}
                data-testid="endpoint-row"
              >
                <TableCell>
                  <code className="text-xs">
                    {e.host}:{e.port}
                  </code>{" "}
                  {transportLabel(e.transport)}
                  {e.transport === Transport.WG_TLS && e.port === 19443 && (
                    <>
                      {" "}
                      <Badge variant="warning">compatibility</Badge>
                    </>
                  )}
                </TableCell>
                <TableCell className="tabular-nums">
                  <div>
                    {fmtDuration(
                      Number(e.liveSeconds) / (Number(e.rotationsAway) + 1),
                    )} mean
                  </div>
                  <div className={e.rotationsAway > 0n ? "text-xs text-amber-700 dark:text-amber-400" : "text-xs text-muted-foreground"}>
                    {fmtDuration(e.liveSeconds)} tunnel · {String(e.rotationsAway)} rotations
                  </div>
                </TableCell>
                <TableCell className="tabular-nums">
                  {e.rxBytes + e.txBytes > 0n
                    ? `${fmtBytes(e.rxBytes)} ↑ / ${fmtBytes(e.txBytes)} ↓`
                    : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {rows.length > COMPACT_ROWS && (
        <Button
          className="mt-2"
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show top 10" : `Show all ${rows.length} endpoints`}
        </Button>
      )}
      <div className="mb-8" />
    </>
  );
}

// WgTunnels is the per-device tunnel view: which endpoint (port + carrier)
// each device actually rides — the observability the camouflage endpoints
// (443/3478) and the WG-over-TCP fallback shipped without — plus handshake
// freshness as worker-observed ground truth. Two independent reporters feed
// it: the device names its endpoint (only it can — DNAT and the TCP proxy
// hide the choice from the worker), the worker's heartbeat names handshakes,
// per-device counters, and the device's source address.
function WgTunnels({ ownerEmail }: { ownerEmail?: string }) {
  const [expanded, setExpanded] = useState(false);
  const devices = useQuery(
    adminListDevices,
    {},
    { retry: false, refetchInterval: 15000 },
  );
  const rows = (devices.data?.devices ?? []).filter(
    (r) =>
      (!ownerEmail || r.ownerEmail === ownerEmail) &&
      ((r.device?.linkReportedUnix ?? 0n) > 0n ||
        (r.device?.wgReportedUnix ?? 0n) > 0n),
  );
  const handshakeOf = (d: (typeof rows)[number]["device"]) =>
    (d?.wgHandshakeUnix ?? 0n) > 0n ? d?.wgHandshakeUnix : d?.linkHandshakeUnix;
  const live = rows.filter((r) => tunnelLive(handshakeOf(r.device)));
  const sorted = [...rows].sort((a, b) => {
    const aHandshake = handshakeOf(a.device) ?? 0n;
    const bHandshake = handshakeOf(b.device) ?? 0n;
    const aLive = tunnelLive(aHandshake);
    const bLive = tunnelLive(bHandshake);
    if (aLive !== bLive) return aLive ? -1 : 1;
    return Number(bHandshake - aHandshake);
  });
  const compact =
    live.length > 0
      ? sorted.filter((r) => tunnelLive(handshakeOf(r.device)))
      : sorted;
  const defaultVisible = compact.slice(0, COMPACT_ROWS);
  const visible = expanded ? sorted : defaultVisible;
  // How the live tunnels split across endpoints — the "did anyone actually
  // land on :443" question, answered per (carrier, port).
  const mix = new Map<string, number>();
  for (const r of live) {
    const d = r.device;
    if (!d || d.linkReportedUnix === 0n) continue;
    const key = `${transportLabel(d.linkTransport)} :${d.linkEndpointPort}`;
    mix.set(key, (mix.get(key) ?? 0) + 1);
  }

  return (
    <>
      <h2 className="mb-3 text-base font-semibold tracking-tight">
        WG tunnels
      </h2>
      <p className="mb-3 text-sm text-muted-foreground">
        Per-device tunnel attachment: the endpoint the device reports using,
        the handshake the worker observes (auto-refreshes; live = handshake
        under 3 minutes). Traffic is the device&apos;s split of the worker
        interface counters — rx ↑ is device upload.
      </p>
      {devices.isPending ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyNote>No devices have reported a tunnel yet.</EmptyNote>
      ) : (
        <>
          <Table aria-label="WG tunnels">
            <TableHeader>
              <TableRow>
                <TableHead>Device</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead>Traffic</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((r) => {
                const d = r.device;
                const handshake = handshakeOf(d);
                return (
                  <TableRow key={d?.id} data-testid="wg-tunnel-row">
                    <TableCell>
                      <div className="font-medium"><TableText value={d?.name || d?.id || "—"} /></div>
                      <div className="text-xs text-muted-foreground">
                        <TableText value={r.ownerEmail || "—"} />
                      </div>
                      <div
                        className={
                          tunnelLive(handshake)
                            ? "text-xs text-muted-foreground"
                            : "text-xs text-amber-700 dark:text-amber-400"
                        }
                        title="Last completed WG handshake, worker-observed where available"
                      >
                        handshake {(handshake ?? 0n) > 0n ? ago(handshake) : "never"}
                        {tunnelLive(handshake) ? " · live" : " · stale"}
                      </div>
                    </TableCell>
                    {/* Which of OUR endpoints carries this tunnel — the
                        question the table exists for. The device's own source
                        address is drill-down material, so it lives in the
                        hover, not a column. */}
                    <TableCell>
                      {d && d.linkReportedUnix > 0n ? (
                        <span
                          title={
                            d.wgEndpoint
                              ? `device source ${d.wgEndpoint}`
                              : undefined
                          }
                        >
                          <code className="text-xs">
                            {d.linkEndpointHost || "?"}:
                            {d.linkEndpointPort || "?"}
                          </code>{" "}
                          {transportLabel(d.linkTransport)}
                        </span>
                      ) : (
                        // The worker sees this tunnel but the device has never
                        // named its endpoint — that report ships in 0.2.36, so
                        // the blank means "old client", and should say so.
                        <span
                          className="text-xs text-muted-foreground"
                          title="The endpoint is self-reported by the device's client; this one predates 0.2.36 (or has not polled since updating)"
                        >
                          needs 0.2.36
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {d && d.wgReportedUnix > 0n
                        ? `${fmtBytes(d.wgRxBytes)} ↑ / ${fmtBytes(d.wgTxBytes)} ↓`
                        : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="wg-mix">
            {live.length} of {rows.length} reporting device
            {rows.length === 1 ? "" : "s"} live
            {mix.size > 0 && (
              <>
                {" "}
                —{" "}
                {[...mix.entries()]
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, n]) => `${k} ×${n}`)
                  .join(" · ")}
              </>
            )}
          </p>
          {defaultVisible.length < rows.length && (
            <Button
              className="mt-2"
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded
                ? "Show live only"
                : `Show all ${rows.length} tunnels (${rows.length - live.length} inactive)`}
            </Button>
          )}
        </>
      )}
      <div className="mb-8" />
    </>
  );
}

function DestTable({
  title,
  rows,
}: {
  title: string;
  rows: {
    kind: string;
    dest: string;
    bytes: bigint;
    connections: bigint;
    dials: bigint;
    dialMsTotal: bigint;
    dialTimeouts: bigint;
    dialFailures: bigint;
  }[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [sortBy, setSortBy] = useState<"traffic" | "problems">("traffic");
  const sorted = [...rows].sort((a, b) => {
    if (sortBy === "problems") {
      const aProblems = a.dialTimeouts + a.dialFailures;
      const bProblems = b.dialTimeouts + b.dialFailures;
      if (aProblems !== bProblems) return aProblems > bProblems ? -1 : 1;
      const aDial = a.dials > 0n ? a.dialMsTotal / a.dials : 0n;
      const bDial = b.dials > 0n ? b.dialMsTotal / b.dials : 0n;
      if (aDial !== bDial) return aDial > bDial ? -1 : 1;
    }
    if (a.bytes !== b.bytes) return a.bytes > b.bytes ? -1 : 1;
    return a.dest.localeCompare(b.dest);
  });
  const visible = expanded ? sorted : sorted.slice(0, COMPACT_ROWS);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {rows.length > 1 && (
          <div className="flex items-center gap-1" aria-label={`${title} sort`}>
            <Button
              variant={sortBy === "traffic" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setSortBy("traffic")}
            >
              Traffic
            </Button>
            <Button
              variant={sortBy === "problems" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setSortBy("problems")}
            >
              Problems
            </Button>
          </div>
        )}
      </div>
      {rows.length === 0 ? (
        <EmptyNote>Nothing here yet.</EmptyNote>
      ) : (
        <Table aria-label={title}>
          <TableHeader>
            <TableRow>
              <TableHead>Destination</TableHead>
              <TableHead>Traffic</TableHead>
              <TableHead title="Average TCP dial latency plus timeout and failure counts; UDP flows carry no latency sample">
                Dial health
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((d) => (
              <TableRow key={`${d.kind}:${d.dest}`} data-testid="dest-row">
                <TableCell>
                  <div className="font-mono text-xs"><TableText value={d.dest} /></div>
                </TableCell>
                <TableCell className="tabular-nums">
                  <div>{fmtBytes(d.bytes)}</div>
                  <div className="text-xs text-muted-foreground">
                    {String(d.connections)} connections
                  </div>
                </TableCell>
                <TableCell
                  className={
                    d.dialTimeouts + d.dialFailures > 0n
                      ? "text-amber-700 dark:text-amber-400"
                      : undefined
                  }
                >
                  <div className="tabular-nums">
                    {d.dials > 0n
                      ? `${String(d.dialMsTotal / d.dials)} ms avg`
                      : "—"}
                  </div>
                  <div className="text-xs" title="Timeouts / other dial failures">
                    {d.dialTimeouts + d.dialFailures > 0n
                      ? `${String(d.dialTimeouts)} timeout · ${String(d.dialFailures)} fail`
                      : "no failures"}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {rows.length > COMPACT_ROWS && (
        <Button
          className="mt-2"
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show top 10" : `Show all ${rows.length} candidates`}
        </Button>
      )}
    </div>
  );
}
